import { AfterViewInit, Component, ElementRef, inject, input, NgZone, OnDestroy, Renderer2, ViewEncapsulation } from '@angular/core';

@Component({
  selector: 'work-ui-visual-scroll-indicator',
  template: ` <ng-content></ng-content>`,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./visual-scroll-indicator.component.scss'],
  standalone: true,
})
export class VisualScrollIndicatorComponent implements AfterViewInit, OnDestroy {
  theme = input<'light' | 'dark'>('dark');
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private ngZone = inject(NgZone);

  private leftIndicator: HTMLElement | undefined;
  private rightIndicator: HTMLElement | undefined;
  private topIndicator: HTMLElement | undefined;
  private bottomIndicator: HTMLElement | undefined;
  private scrollContent: HTMLElement | undefined;
  private resizeObserver: ResizeObserver | undefined;

  ngAfterViewInit(): void {
    const host = this.el.nativeElement;
    host.style.position = 'relative';
    this.scrollContent = host.firstElementChild;

    if (!this.scrollContent) return;

    this.renderer.addClass(this.scrollContent, 'scroll-content');

    this.leftIndicator = this.createIndicator('left');
    this.rightIndicator = this.createIndicator('right');
    this.topIndicator = this.createIndicator('top');
    this.bottomIndicator = this.createIndicator('bottom');

    this.renderer.appendChild(host, this.leftIndicator);
    this.renderer.appendChild(host, this.rightIndicator);
    this.renderer.appendChild(host, this.topIndicator);
    this.renderer.appendChild(host, this.bottomIndicator);

    this.ngZone.runOutsideAngular(() => {
      if (this.scrollContent) {
        this.scrollContent.addEventListener('scroll', this.scrollListener);
        this.resizeObserver = new ResizeObserver(() => this.updateIndicators());
        this.resizeObserver.observe(this.scrollContent);
      }
    });

    this.updateIndicators();
  }

  ngOnDestroy(): void {
    if (this.scrollContent) {
      this.scrollContent.removeEventListener('scroll', this.scrollListener);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private scrollListener = () => this.updateIndicators();

  private createIndicator(side: 'left' | 'right' | 'top' | 'bottom'): HTMLElement {
    const indicator = this.renderer.createElement('div');
    switch (side) {
      case 'left':
      case 'right':
        this.renderer.addClass(indicator, 'indicator-horizontal');
        break;
      case 'top':
      case 'bottom':
        this.renderer.addClass(indicator, 'indicator-vertical');
        break;
    }
    this.renderer.addClass(indicator, `indicator-${side}`);
    this.renderer.addClass(indicator, `indicator--${this.theme()}`);
    return indicator;
  }

  private updateIndicators(): void {
    if (this.scrollContent) {
      const scrollLeft = this.scrollContent.scrollLeft;
      const maxScrollLeft = this.scrollContent.scrollWidth - this.scrollContent.clientWidth;
      const scrollTop = this.scrollContent.scrollTop;
      const maxScrollTop = this.scrollContent.scrollHeight - this.scrollContent.clientHeight;

      if (scrollLeft > 0) {
        this.renderer.addClass(this.leftIndicator, 'indicator--visible');
      } else {
        this.renderer.removeClass(this.leftIndicator, 'indicator--visible');
      }
      if (scrollLeft < maxScrollLeft) {
        this.renderer.addClass(this.rightIndicator, 'indicator--visible');
      } else {
        this.renderer.removeClass(this.rightIndicator, 'indicator--visible');
      }
      if (scrollTop > 0) {
        this.renderer.addClass(this.topIndicator, 'indicator--visible');
      } else {
        this.renderer.removeClass(this.topIndicator, 'indicator--visible');
      }
      if (scrollTop < maxScrollTop) {
        this.renderer.addClass(this.bottomIndicator, 'indicator--visible');
      } else {
        this.renderer.removeClass(this.bottomIndicator, 'indicator--visible');
      }
    }
  }
}
